import clsx from "clsx"
import gsap from "gsap"
import { useGSAP } from "@gsap/react"
import { toast } from "react-toastify"
import * as Sentry from "@sentry/react"
import { Draggable } from "gsap/Draggable"
import { FC, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "store/index"
import {
  FeaturesSelectors,
  setPersonalDailyDealEnabled
} from "store/slices/featuresSlice"
import { sendDiagnosticReport } from "utils/diagnostics"
import { getPortalContainer } from "utils/getPortalContainer"
import { Switch } from "ui/switch"
import { ExperimentsSection } from "./ui/ExperimentsSection"
import styles from "./index.module.scss"
import { startGpuStress, stopGpuStress, toggleEruda } from "./helpers"

export const DevToolsButton: FC = () => {
  const isOpenRef = useRef(false)
  const buttonRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const clearBtnRef = useRef<HTMLButtonElement>(null)
  const reportBtnRef = useRef<HTMLButtonElement>(null)
  const isHoldingRef = useRef<boolean>(false)
  const isHoldingReportRef = useRef<boolean>(false)

  const [isSendingReport, setIsSendingReport] = useState<boolean>(false)

  const [isStressActive, setIsStressActive] = useState<boolean>(false)
  const [stressCount, setStressCount] = useState<number>(0)

  const dispatch = useAppDispatch()
  const isPersonalDailyDealEnabled = useAppSelector(
    FeaturesSelectors.selectIsPersonalDailyDealEnabled()
  )

  useGSAP(() => {
    if (!buttonRef.current) return

    Draggable.create(buttonRef.current, {
      type: "x,y",
      bounds: getPortalContainer(),
      inertia: true,
      onClick: handleOpen
    })

    gsap.set(overlayRef.current, { autoAlpha: 0 })
    gsap.set(panelRef.current, { autoAlpha: 0, scale: 0 })
  }, [])

  const handleOpen = () => {
    if (isOpenRef.current || !panelRef.current || !overlayRef.current) return
    isOpenRef.current = true

    gsap.to(overlayRef.current, { autoAlpha: 1, duration: 0.2 })
    gsap.fromTo(
      panelRef.current,
      { scale: 0 },
      { scale: 1, autoAlpha: 1, duration: 0.35, ease: "back.out(1.4)" }
    )
  }

  const cancelHold = () => {
    isHoldingRef.current = false
    clearBtnRef.current?.classList.remove(styles.clearBtn_holding)
  }

  const cancelHoldReport = () => {
    isHoldingReportRef.current = false
    reportBtnRef.current?.classList.remove(styles.reportBtn_holding)
  }

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.propertyName === "width" && isHoldingRef.current) {
      localStorage.clear()
      sessionStorage.clear()
      window.location.reload()
    }
  }

  const handleToggleStress = () => {
    if (isStressActive) {
      stopGpuStress()
      setIsStressActive(false)
    } else {
      startGpuStress(stressCount)
      setIsStressActive(true)
    }
  }

  const handleReportTransitionEnd = async (
    e: React.TransitionEvent<HTMLDivElement>
  ) => {
    if (e.propertyName !== "width" || !isHoldingReportRef.current) return
    if (isSendingReport) return

    cancelHoldReport()
    setIsSendingReport(true)
    try {
      await sendDiagnosticReport()
      toast.success("Report sent")
    } catch (err) {
      Sentry.captureException(err)
      toast.error("Failed to send report")
    } finally {
      setIsSendingReport(false)
    }
  }

  const handleClose = () => {
    if (!isOpenRef.current || !panelRef.current || !overlayRef.current) return

    gsap.to(overlayRef.current, { autoAlpha: 0, duration: 0.25 })
    gsap.to(panelRef.current, {
      scale: 0,
      autoAlpha: 0,
      duration: 0.25,
      ease: "power2.in",
      onComplete: () => {
        isOpenRef.current = false
      }
    })
  }

  return (
    <>
      <div ref={buttonRef} className={styles.button}>
        <div className={styles.inner}>
          <span className={styles.icon}>⚙</span>
        </div>
      </div>

      <div ref={overlayRef} className={styles.overlay} onClick={handleClose}>
        <div
          ref={panelRef}
          className={styles.panel}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>⚙ LABR DEV</span>
            <button className={styles.panelClose} onClick={handleClose}>
              ✕
            </button>
          </div>
          <div className={styles.panelBody}>
            <div className={styles.toggleRow}>
              <span className={styles.toggleLabel}>Personal Daily Deal</span>
              <Switch
                checked={isPersonalDailyDealEnabled}
                onChange={(e) =>
                  dispatch(setPersonalDailyDealEnabled(e.target.checked))
                }
              />
            </div>
            <div className={styles.toggleRow}>
              <span className={styles.toggleLabel}>GPU stress layers</span>
              <input
                type="range"
                min={0}
                max={400}
                step={10}
                value={stressCount}
                disabled={isStressActive}
                onChange={(e) => setStressCount(Number(e.target.value))}
                className={styles.stressSlider}
              />
              <span className={styles.stressValue}>{stressCount}</span>
            </div>
            <button
              className={clsx(
                styles.stressBtn,
                isStressActive && styles.stressBtn_active
              )}
              disabled={!isStressActive && stressCount === 0}
              onClick={handleToggleStress}
            >
              <span className={styles.stressBtnLabel}>
                {isStressActive
                  ? `Stop GPU stress (${stressCount})`
                  : `Start GPU stress (${stressCount})`}
              </span>
            </button>
            <button
              className={styles.erudaBtn}
              onClick={() => {
                toggleEruda()
                handleClose()
              }}
            >
              <span className={styles.erudaBtnLabel}>Open Eruda console</span>
            </button>
            <button
              ref={reportBtnRef}
              className={styles.reportBtn}
              disabled={isSendingReport}
              onPointerDown={() => {
                if (isSendingReport) return
                isHoldingReportRef.current = true
                reportBtnRef.current?.classList.add(styles.reportBtn_holding)
              }}
              onPointerUp={cancelHoldReport}
              onPointerLeave={cancelHoldReport}
            >
              <div
                className={styles.reportBtnProgress}
                onTransitionEnd={handleReportTransitionEnd}
              />
              <span className={styles.reportBtnLabel}>
                {isSendingReport
                  ? "Sending..."
                  : "Hold to send diagnostic report"}
              </span>
            </button>
            <button
              ref={clearBtnRef}
              className={styles.clearBtn}
              onPointerDown={() => {
                isHoldingRef.current = true
                clearBtnRef.current?.classList.add(styles.clearBtn_holding)
              }}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
            >
              <div
                className={styles.clearBtnProgress}
                onTransitionEnd={handleTransitionEnd}
              />
              <span className={styles.clearBtnLabel}>
                Hold to clear storage
              </span>
            </button>
            <ExperimentsSection />
          </div>
          <div className={styles.panelFooter}>
            v{import.meta.env.VITE_APP_VERSION} ·{" "}
            {import.meta.env.VITE_NODE_ENV}
          </div>
        </div>
      </div>
    </>
  )
}

export default DevToolsButton
